import { defineStore } from 'pinia'

import { apiClientDB } from '@/config_sessions'

// A store's GPS location (weather) and the holiday calendars it follows.
// Backend: /location_calendar/, /holiday_regions, /sector_calendars/ (api_db.py).

const auth = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } })

const message = (err, fallback) => err?.response?.data?.message || fallback

export const useLocationCalendarStore = defineStore({
    id: 'locationcalendar',

    state: () => ({
        current: null,          // GET /location_calendar/ payload
        regionsByCountry: {},   // 'BE' -> [{code, name, type}]
        sectorCalendars: [],
        loading: false,
    }),

    actions: {
        async fetchCurrent() {
            this.loading = true
            try {
                const res = await apiClientDB.get('/location_calendar/', auth())
                this.current = res.data
                return res.data
            } finally {
                this.loading = false
            }
        },

        async save(payload) {
            try {
                const res = await apiClientDB.put('/location_calendar/', payload, auth())
                this.current = res.data
                return res.data
            } catch (err) {
                throw new Error(message(err, 'Could not save location & calendar'))
            }
        },

        async fetchRegions(countryCode) {
            if (!countryCode) return []
            if (this.regionsByCountry[countryCode]) return this.regionsByCountry[countryCode]
            try {
                const res = await apiClientDB.get('/holiday_regions', { ...auth(), params: { country: countryCode } })
                this.regionsByCountry[countryCode] = res.data.regions || []
                return this.regionsByCountry[countryCode]
            } catch (err) {
                throw new Error(message(err, 'Holiday regions are unavailable right now'))
            }
        },

        // Super-admins: every sector-calendar period (flat), and which stores follow each.
        async fetchSectorCalendarsAdmin() {
            const res = await apiClientDB.get('/sector_calendars/', auth())
            return { entries: res.data.entries || [], stores: res.data.stores || {} }
        },

        async addSectorPeriod(entry) {
            try {
                await apiClientDB.post('/sector_calendars/', entry, auth())
            } catch (err) {
                throw new Error(message(err, 'Could not add the period'))
            }
        },

        async removeSectorPeriod(id) {
            try {
                await apiClientDB.delete(`/sector_calendars/${id}/`, auth())
            } catch (err) {
                throw new Error(message(err, 'Could not remove the period'))
            }
        },

        async fetchSectorCalendars() {
            try {
                const res = await apiClientDB.get('/sector_calendars/', auth())
                this.sectorCalendars = res.data.calendars || []
            } catch (err) {
                this.sectorCalendars = []
            }
            return this.sectorCalendars
        },
    },
})
